type ConsentListener = () => void;

const listeners = new Set<ConsentListener>();
let consentEventsRegistered = false;
// Set when the visitor clicks "accept" on a placeholder while Cookiebot itself is
// missing (blocked or failed to load): there is no consent object to update, but the
// visitor asked for the video explicitly.
let grantedWithoutCookiebot = false;

const YOUTUBE_HOSTS = /(^|\.)(youtube\.com|youtube-nocookie\.com|youtu\.be|ytimg\.com|googlevideo\.com)$/i;

// Only the editor is exempt. Whether the Cookie rendering emitted a snippet says nothing about
// consent: GTM injects its own Cookiebot on pages whose snippet is empty.
const isEditing = (): boolean =>
  window._THULEDATA?.IsEditing === true || (window as any)._EXTRANETDATA?.IsEditing === true;

export const hasMarketingConsent = (): boolean =>
  isEditing() || grantedWithoutCookiebot || window.Cookiebot?.consent?.marketing === true;

const flushIfGranted = (): void => {
  if (!hasMarketingConsent()) return;
  const pending = [...listeners];
  listeners.clear();
  pending.forEach((listener) => listener());
};

const registerConsentEvents = (): void => {
  if (consentEventsRegistered) return;
  consentEventsRegistered = true;
  ['CookiebotOnConsentReady', 'CookiebotOnAccept'].forEach((eventName) =>
    window.addEventListener(eventName, flushIfGranted)
  );
};

export const onMarketingConsent = (listener: ConsentListener): (() => void) => {
  if (hasMarketingConsent()) {
    listener();
    return () => {};
  }
  registerConsentEvents();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const requestMarketingConsent = (): void => {
  const cookiebot = window.Cookiebot;
  if (typeof cookiebot?.submitCustomConsent === 'function') {
    cookiebot.submitCustomConsent(
      cookiebot.consent?.preferences === true,
      cookiebot.consent?.statistics === true,
      true
    );
  } else {
    grantedWithoutCookiebot = true;
  }
  flushIfGranted();
};

export const isYoutubeHosted = (url?: string | null): boolean => {
  if (!url) return false;
  try {
    return YOUTUBE_HOSTS.test(new URL(url, window.location.origin).hostname);
  } catch {
    return false;
  }
};

export const CONSENT_GATED_SELECTOR = '.js-youtube-embed[data-consent-src]';
export const CONSENT_PLACEHOLDER_SELECTOR = '.js-video-placeholder';

const isDisplayed = (element: HTMLElement): boolean => element.getClientRects().length > 0;

const consentScope = (element: HTMLElement): HTMLElement | null =>
  element.closest<HTMLElement>('[data-consent-scope]') ?? element.parentElement;

export const activateConsentGatedElements = (root: ParentNode = document): void => {
  if (!hasMarketingConsent()) return;

  root.querySelectorAll<HTMLElement>(CONSENT_GATED_SELECTOR).forEach((element) => {
    const scope = consentScope(element);
    // Iframes start a player, so only the copy that is displayed loads (Video embed
    // renders a desktop and a mobile iframe); images are cheap and hidden tabs need them.
    if (element instanceof HTMLIFrameElement && scope && !isDisplayed(scope)) return;

    const src = element.dataset.consentSrc;
    if (src) element.setAttribute('src', src);
    delete element.dataset.consentSrc;
    element.hidden = false;
    scope?.querySelectorAll<HTMLElement>(CONSENT_PLACEHOLDER_SELECTOR).forEach((placeholder) => {
      placeholder.hidden = true;
    });
  });
};
